import {
  Button,
  Dropdown,
  Field,
  Input,
  Option,
  Tooltip,
} from '@fluentui/react-components';
import {
  Add20Filled,
  Add20Regular,
  bundleIcon,
  Delete20Filled,
  Delete20Regular,
} from '@fluentui/react-icons';

import type {
  VariableRow,
  VariablesEditorProps,
} from './VariablesEditor.interface.js';
import { useStyles } from './VariablesEditor.style.js';

/* eslint-disable @typescript-eslint/no-unsafe-assignment */
const AddIcon = bundleIcon(Add20Filled, Add20Regular);
const DeleteIcon = bundleIcon(Delete20Filled, Delete20Regular);
/* eslint-enable @typescript-eslint/no-unsafe-assignment */

const DEFAULT_NAME_PATTERN = /^[A-Z][A-Z0-9_]*$/;
const DEFAULT_NAME_ERROR = 'Use UPPER_SNAKE_CASE.';

function rowError<T extends string>(
  row: VariableRow<T>,
  errors: Record<string, string> | undefined,
  namePattern: RegExp,
  nameErrorMessage: string,
): string | undefined {
  // Controlled mode: the parent owns validation.
  if (errors !== undefined) return errors[row.id];
  // Uncontrolled mode: validate a non-empty name against the pattern.
  if (row.name.length > 0 && !namePattern.test(row.name)) {
    return nameErrorMessage;
  }
  return undefined;
}

export function VariablesEditor<T extends string = string>({
  variables,
  typeOptions,
  disabled = false,
  errors,
  namePattern = DEFAULT_NAME_PATTERN,
  nameErrorMessage = DEFAULT_NAME_ERROR,
  emptyLabel,
  onUpdate,
  onAdd,
  onRemove,
}: VariablesEditorProps<T>) {
  const styles = useStyles();

  return (
    <div className={styles.root}>
      {variables.length === 0 && emptyLabel !== undefined && (
        <span className={styles.empty}>{emptyLabel}</span>
      )}
      {variables.length > 0 && (
        <div className={styles.header}>
          <span>Name</span>
          <span>Type</span>
          <span>Default</span>
          <span>Description</span>
          <span aria-hidden />
        </div>
      )}
      {variables.map((v) => {
        const error = rowError(v, errors, namePattern, nameErrorMessage);
        return (
          <div key={v.id} className={styles.row}>
            <Field
              validationState={error === undefined ? 'none' : 'error'}
              validationMessage={error}
            >
              <Input
                value={v.name}
                onChange={(_e, data) => onUpdate(v.id, { name: data.value })}
                placeholder="MY_VAR"
                aria-label="Variable name"
                disabled={disabled}
              />
            </Field>
            {disabled ? (
              <Input
                value={
                  typeOptions.find((option) => option.value === v.type)
                    ?.label ?? v.type
                }
                aria-label="Variable type"
                disabled
              />
            ) : (
              <Dropdown
                value={v.type}
                selectedOptions={[v.type]}
                onOptionSelect={(_e, data) => {
                  if (data.optionValue) {
                    onUpdate(v.id, { type: data.optionValue as T });
                  }
                }}
                aria-label="Variable type"
              >
                {typeOptions.map((o) => (
                  <Option key={o.value} value={o.value} text={o.label}>
                    {o.label}
                  </Option>
                ))}
              </Dropdown>
            )}
            <Input
              value={v.defaultValue}
              onChange={(_e, data) =>
                onUpdate(v.id, { defaultValue: data.value })
              }
              aria-label="Variable default"
              disabled={disabled}
            />
            <Input
              value={v.description}
              onChange={(_e, data) =>
                onUpdate(v.id, { description: data.value })
              }
              aria-label="Variable description"
              disabled={disabled}
            />
            {!disabled && (
              <Tooltip content="Remove" relationship="label">
                <Button
                  appearance="subtle"
                  icon={<DeleteIcon />}
                  onClick={() => onRemove(v.id)}
                  aria-label={`Remove variable ${v.name || v.id}`}
                />
              </Tooltip>
            )}
          </div>
        );
      })}
      {!disabled && (
        <div>
          <Button icon={<AddIcon />} onClick={onAdd}>
            Add variable
          </Button>
        </div>
      )}
    </div>
  );
}
